import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { showToast } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import StatusBadge from '../components/StatusBadge';
import { DSCard, DSPageHeader, DSSplitButton, DSInput, DSSelect, DSTextarea, DSStatTile, DSIcon, DSButton } from '../design-system/primitives';
import { useDevices } from '../hooks/useDevices';
import DeveloperBottomNav from '../components/DeveloperBottomNav';
import './Devices.css';

function Devices() {
  const {
    devices,
    loading,
    error,
    loadDevices,
    deviceDetails,
    dailyData,
    playerSpend,
    loadingDetails,
    loadDeviceDetails,
    clearDeviceDetails,
    registerDevice,
    fetchDeviceRecord,
    deleteDevice,
    toggleDeviceActive,
    fetchDeviceLogs,
    fetchDeviceReconciliations,
    reconcileDevice,
    updateDeviceSettings
  } = useDevices();
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDevice, setNewDevice] = useState({
    deviceName: '',
    deviceType: 'pinball',
    location: ''
  });
  const [newDeviceApiKey, setNewDeviceApiKey] = useState(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showInactiveDevices, setShowInactiveDevices] = useState(false);
  
  // Revenue details state
  const [expandedDeviceId, setExpandedDeviceId] = useState(null);
  const [dateRange, setDateRange] = useState(30);

  // Reconciliation state
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [deviceToReconcile, setDeviceToReconcile] = useState(null);
  const [reconciliationData, setReconciliationData] = useState({
    reportedPhysicalCount: '',
    note: ''
  });
  const [reconciliations, setReconciliations] = useState([]);
  const [showReconciliationHistory, setShowReconciliationHistory] = useState(false);
  
  // Edit device state
  const [showEditModal, setShowEditModal] = useState(false);
  const [deviceToEdit, setDeviceToEdit] = useState(null);
  const [editData, setEditData] = useState({
    revenueTrackingStartDate: ''
  });
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsDevice, setLogsDevice] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Confirm modal state (replaces window.confirm)
  const [confirmState, setConfirmState] = useState(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const handleToggleDetails = async (deviceId) => {
    if (expandedDeviceId === deviceId) {
      // Collapse
      setExpandedDeviceId(null);
      clearDeviceDetails();
    } else {
      // Expand
      setExpandedDeviceId(deviceId);
      await loadDeviceDetails(deviceId, dateRange);
    }
  };

  const formatCurrency = (value) => {
    return `$${parseFloat(value || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Reload details when date range changes
  useEffect(() => {
    if (expandedDeviceId) {
      loadDeviceDetails(expandedDeviceId, dateRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const handleAddDevice = async () => {
    if (!newDevice.deviceName || !newDevice.deviceType) {
      showToast('Please fill in required fields', 'warning');
      return;
    }

    try {
      const apiKey = await registerDevice(newDevice);
      setNewDeviceApiKey(apiKey);
      // Don't close modal - show API key first
    } catch (err) {
      showToast('Failed to register device', 'error');
      console.error(err);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewDevice({ deviceName: '', deviceType: 'pinball', location: '' });
    setNewDeviceApiKey(null);
  };

  const handleViewApiKey = async (device) => {
    try {
      // Fetch full device details including API key
      const data = await fetchDeviceRecord(device.id);
      setSelectedDevice(data);
      setShowApiKeyModal(true);
    } catch (err) {
      showToast('Failed to load device details', 'error');
      console.error(err);
    }
  };

  const handleDeleteDevice = (device) => {
    setDeviceToDelete(device);
    setDeleteConfirmation('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deviceToDelete) return;

    try {
      await deleteDevice(deviceToDelete.id, deleteConfirmation);
      showToast(`Device "${deviceToDelete.device_name}" has been deleted successfully`, 'success');
      setShowDeleteModal(false);
      setDeviceToDelete(null);
      setDeleteConfirmation('');
    } catch (err) {
      if (err.response?.data?.error) {
        showToast(err.response.data.error, 'error');
      } else {
        showToast('Failed to delete device', 'error');
      }
      console.error(err);
    }
  };

  const handleToggleActive = (device) => {
    const action = device.is_active ? 'deactivate' : 'activate';
    setConfirmState({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Device?`,
      message: `Are you sure you want to ${action} "${device.device_name}"?`,
      confirmLabel: action.charAt(0).toUpperCase() + action.slice(1),
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await toggleDeviceActive(device);
          showToast(`Device "${device.device_name}" has been ${device.is_active ? 'deactivated' : 'activated'}`, 'success');
          setConfirmState(null);
        } catch (err) {
          showToast(`Failed to ${action} device`, 'error');
          console.error(err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const handleViewLogs = async (device) => {
    try {
      setLogsDevice(device);
      setShowLogsModal(true);
      setLogsLoading(true);
      const logs = await fetchDeviceLogs(device.id);
      setRecentLogs(logs);
    } catch (err) {
      showToast('Failed to load device logs', 'error');
      console.error(err);
      setRecentLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleReconcile = async (device) => {
    setDeviceToReconcile(device);
    setReconciliationData({
      reportedPhysicalCount: '',
      note: ''
    });
    setShowReconcileModal(true);

    // Load reconciliation history
    try {
      const recs = await fetchDeviceReconciliations(device.id);
      setReconciliations(recs);
    } catch (err) {
      console.error('Failed to load reconciliation history:', err);
    }
  };

  const submitReconciliation = async () => {
    if (!deviceToReconcile || !reconciliationData.reportedPhysicalCount) {
      showToast('Please enter the physical play count from the machine', 'warning');
      return;
    }

    const count = parseInt(reconciliationData.reportedPhysicalCount);
    if (isNaN(count) || count < 0) {
      showToast('Please enter a valid positive number', 'warning');
      return;
    }

    try {
      const data = await reconcileDevice(deviceToReconcile.id, {
        reportedPhysicalCount: count,
        note: reconciliationData.note
      });

      if (data.warning) {
        showToast(`Warning: ${data.warning}`, 'warning');
      } else {
        showToast(
          `Reconciliation successful!\n\n` +
          `Physical plays: ${data.summary.physicalDelta}\n` +
          `QR plays: ${data.summary.qrPlaysSinceLastReconciliation}\n` +
          `Untracked plays added: ${data.summary.calculatedUntrackedPlays}`,
          'success'
        );
      }

      setShowReconcileModal(false);
      setDeviceToReconcile(null);
      setReconciliationData({ reportedPhysicalCount: '', note: '' });

      // Reload details if expanded (device list already reloaded by reconcileDevice)
      if (expandedDeviceId === deviceToReconcile.id) {
        await loadDeviceDetails(deviceToReconcile.id, dateRange);
      }
    } catch (err) {
      if (err.response?.data?.error) {
        showToast(`Error: ${err.response.data.error}`, 'error');
      } else {
        showToast('Failed to reconcile device', 'error');
      }
      console.error(err);
    }
  };

  const handleEditDevice = async (device) => {
    try {
      // Fetch full device details to get current revenue_goal
      const data = await fetchDeviceRecord(device.id);
      setDeviceToEdit(data);
      setEditData({
        revenueTrackingStartDate: data.revenue_tracking_start_date || '',
        revenueGoal: data.revenue_goal || 1500
      });
      setShowEditModal(true);
    } catch (err) {
      showToast('Unable to load device settings. Please try again or refresh the page.', 'error');
      console.error(err);
    }
  };

  const submitDeviceEdit = async () => {
    if (!deviceToEdit) return;

    // Validate revenue goal
    const revenueGoal = parseFloat(editData.revenueGoal);
    if (isNaN(revenueGoal) || revenueGoal <= 0) {
      showToast('Revenue goal must be a positive number greater than 0 (e.g., 1500.00)', 'warning');
      return;
    }

    try {
      await updateDeviceSettings(deviceToEdit.id, {
        revenueTrackingStartDate: editData.revenueTrackingStartDate,
        revenueGoal: revenueGoal
      });

      showToast('Device updated successfully!', 'success');
      setShowEditModal(false);
      setDeviceToEdit(null);

      // Reload details if expanded (device list already reloaded by updateDeviceSettings)
      if (expandedDeviceId === deviceToEdit.id) {
        await loadDeviceDetails(deviceToEdit.id, dateRange);
      }
    } catch (err) {
      showToast('Failed to update device', 'error');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="page bn-page-padding">
        <div className="loading">Loading devices...</div>
        <DeveloperBottomNav />
      </div>
    );
  }

  const getProgressClass = (percent) => {
    if (percent >= 100) return 'complete';
    if (percent >= 50) return 'high';
    if (percent >= 25) return 'medium';
    return 'low';
  };

  const filteredDevices = showInactiveDevices
    ? devices
    : devices.filter((device) => device.is_active);

  return (
    <div className="page devices-page bn-page-padding">
      <DSPageHeader title="Devices" description="Manage Raspberry Pi devices and access points" />

      {error && <div className="device-alert error">{error}</div>}

      <div className="devices-header">
        <div className="devices-header-row">
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <DSButton tone="secondary" appearance="solid" onClick={() => setShowInactiveDevices((prev) => !prev)}>
              {showInactiveDevices
                ? 'Hide Inactive'
                : `Show Inactive (${devices.filter((device) => !device.is_active).length})`}
            </DSButton>
            <DSButton tone="primary" appearance="solid" className="desktop-add-btn" onClick={() => setShowAddModal(true)}>
              + Add Device
            </DSButton>
          </div>
        </div>
      </div>

      {filteredDevices.length > 0 ? (
        <div className="devices-grid">
          {filteredDevices.map(device => {
            const progressPercent = parseFloat(device.progress_percent || 0);
            return (
              <DSCard
                key={device.id}
                cornerVariant="lg"
                className="device-card"
                title={device.device_name}
                subtitle={<span className="device-card-type">{device.device_type}</span>}
                actions={(
                  <div className="device-card-status-stack">
                    <StatusBadge variant={device.is_active ? 'success' : 'error'}>
                      {device.is_active ? <><DSIcon name="check" /> Active</> : <><DSIcon name="x" /> Inactive</>}
                    </StatusBadge>
                    <StatusBadge variant={device.isOnline ? 'success' : 'error'}>
                      {device.isOnline ? <><DSIcon name="dot" /> Online</> : <><DSIcon name="dot" /> Offline</>}
                    </StatusBadge>
                  </div>
                )}
              >
                {device.location && (
                  <div className="device-card-location">
                    {device.location}
                  </div>
                )}

                <div className="device-revenue-section">
                  <div className="device-revenue-row">
                    <div className="device-revenue-item">
                      <div className="device-revenue-label">Total Revenue</div>
                      <div className="device-revenue-value">
                        ${parseFloat(device.total_revenue || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="device-revenue-item">
                      <div className="device-revenue-label">Today</div>
                      <div className="device-revenue-value today">
                        ${parseFloat(device.today_revenue || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="device-progress">
                    <div className="device-progress-label">
                      <span>Progress to ${parseFloat(device.revenue_goal || 1500).toFixed(0)} Goal</span>
                      <span style={{ fontWeight: 700 }}>{progressPercent.toFixed(1)}%</span>
                    </div>
                    <div className="device-progress-bar-container">
                      <div 
                        className={`device-progress-bar ${getProgressClass(progressPercent)}`}
                        style={{ width: `${Math.min(progressPercent, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                <div className="device-card-meta">
                  <div className="device-card-meta-item">
                    <div className="device-card-meta-label">Device IP</div>
                    <div>{device.local_ip || 'Unknown'}</div>
                  </div>
                  <div className="device-card-meta-item">
                    <div className="device-card-meta-label">Last Sync</div>
                    <div>{device.last_sync ? new Date(device.last_sync).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}</div>
                  </div>
                  <div className="device-card-meta-item">
                    <div className="device-card-meta-label">Created</div>
                    <div>{device.created_at ? new Date(device.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown'}</div>
                  </div>
                </div>

                <div className="device-card-actions">
                  <DSSplitButton
                    label={expandedDeviceId === device.id ? 'Hide Details' : 'View Details'} icon={<DSIcon name="target" />}
                    onMainClick={() => handleToggleDetails(device.id)}
                    tone="primary"
                    appearance="solid"
                    actions={[
                      {
                        label: 'Reconcile', icon: <DSIcon name="refresh" />,
                        onClick: () => handleReconcile(device)
                      },
                      {
                        label: 'Edit', icon: <DSIcon name="edit" />,
                        onClick: () => handleEditDevice(device)
                      },
                      {
                        label: 'API Key', icon: <DSIcon name="key" />,
                        onClick: () => handleViewApiKey(device)
                      },
                      {
                        label: <><DSIcon name="scroll" /> Recent Logs</>,
                        onClick: () => handleViewLogs(device)
                      },
                      {
                        label: device.is_active ? <><DSIcon name="pause" /> Deactivate</> : <><DSIcon name="chevronRight" /> Activate</>,
                        onClick: () => handleToggleActive(device)
                      },
                      {
                        label: 'Delete', icon: <DSIcon name="trash" />,
                        onClick: () => handleDeleteDevice(device),
                        tone: 'danger'
                      }
                    ]}
                  />
                </div>

                {/* Expanded Revenue Analytics */}
                {expandedDeviceId === device.id && deviceDetails && (
                  <div className="device-analytics-section">
                    {/* Key Metrics */}
                    <div className="stats-grid" style={{ marginTop: '20px' }}>
                      <DSStatTile
                        labelPosition="top"
                        label="Total Revenue"
                        value={formatCurrency(deviceDetails.total_revenue)}
                        subtext={`of ${formatCurrency(deviceDetails.revenue_goal || 1500)} goal`}
                      />

                      <DSStatTile
                        labelPosition="top"
                        label="Today's Revenue"
                        value={formatCurrency(deviceDetails.today_revenue)}
                        subtext="current day"
                      />

                      <DSStatTile
                        labelPosition="top"
                        label="Daily Average"
                        value={formatCurrency(deviceDetails.avg_daily_revenue)}
                        subtext={(
                          <>
                            over {deviceDetails.days_active} days
                            {deviceDetails.revenue_goal && deviceDetails.days_active > 0 && (() => {
                              // Target daily rate should be goal / 90 days, not goal / days_active
                              const targetDailyRate = parseFloat(deviceDetails.revenue_goal) / 90;
                              return (
                                <>
                                  <br />
                                  <span style={{
                                    color: parseFloat(deviceDetails.avg_daily_revenue) >= targetDailyRate ? 'var(--ds-color-statusSuccessText)' : 'var(--ds-color-statusErrorText)',
                                    fontWeight: '600'
                                  }}>
                                    Target: {formatCurrency(targetDailyRate)}/day
                                  </span>
                                </>
                              );
                            })()}
                          </>
                        )}
                      />

                      <DSStatTile
                        labelPosition="top"
                        label="Progress to Goal"
                        value={`${parseFloat(deviceDetails.progress_percent).toFixed(1)}%`}
                        subtext={`${formatCurrency(deviceDetails.revenue_remaining)} remaining`}
                      />

                      <DSStatTile
                        labelPosition="top"
                        label="Est. Days to Goal"
                        value={deviceDetails.estimated_days_to_goal !== null
                          ? Math.round(deviceDetails.estimated_days_to_goal)
                          : 'N/A'}
                        subtext={(
                          <>
                            at current rate
                            {deviceDetails.estimated_days_to_goal !== null && deviceDetails.days_active > 0 && (() => {
                              // Calculate expected revenue at this point in the 90-day period
                              const revenueGoal = parseFloat(deviceDetails.revenue_goal);
                              const targetDailyRate = revenueGoal / 90;
                              const expectedRevenue = targetDailyRate * deviceDetails.days_active;
                              const actualRevenue = parseFloat(deviceDetails.total_revenue);
                              const revenueDifference = actualRevenue - expectedRevenue;
                              const daysDifference = Math.abs(revenueDifference / targetDailyRate);

                              return (
                                <>
                                  <br />
                                  <span style={{
                                    color: revenueDifference >= 0 ? 'var(--ds-color-statusSuccessText)' : 'var(--ds-color-statusErrorText)',
                                    fontWeight: '600'
                                  }}>
                                    {revenueDifference >= 0
                                      ? `${Math.round(daysDifference)} days ahead of 90-day goal`
                                      : `${Math.round(daysDifference)} days behind 90-day goal`
                                    }
                                  </span>
                                </>
                              );
                            })()}
                          </>
                        )}
                      />
                    </div>

                    {/* Player Spend Breakdown */}
                    {playerSpend && playerSpend.totalPlays > 0 && (
                      <DSCard cornerVariant="md" style={{ marginTop: '20px' }} title="Player Spend Breakdown">
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--ds-color-surface)', zIndex: 1 }}>
                              <tr style={{ borderBottom: '2px solid var(--ds-color-borderSubtle)' }}>
                                <th style={{ padding: '10px', textAlign: 'left' }}>Player</th>
                                <th style={{ padding: '10px', textAlign: 'right' }}>Total Spent</th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...playerSpend.players]
                                .sort((a, b) => (parseFloat(b.total_spent || 0) - parseFloat(a.total_spent || 0)))
                                .map((player) => (
                                <tr key={player.user_id} style={{ borderBottom: '1px solid var(--ds-color-borderSubtle)' }}>
                                  <td style={{ padding: '10px', fontWeight: '500' }}>
                                    {player.first_name && player.last_name 
                                      ? `${player.first_name} ${player.last_name}` 
                                      : player.username}
                                  </td>
                                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: '500' }}>
                                    {formatCurrency(player.total_spent)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot style={{ borderTop: '2px solid var(--ds-color-borderSubtle)', fontWeight: 'bold' }}>
                              <tr>
                                <td style={{ padding: '10px', textAlign: 'right' }}>Total:</td>
                                <td style={{ padding: '10px', textAlign: 'right' }}>
                                  {formatCurrency(
                                    playerSpend.players.reduce(
                                      (sum, player) => sum + parseFloat(player.total_spent || 0),
                                      0
                                    )
                                  )}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </DSCard>
                    )}

                    {/* Daily Revenue Chart */}
                    <DSCard
                      cornerVariant="md"
                      style={{ marginTop: '20px' }}
                      title="Revenue Over Time"
                      actions={(
                        <div className="date-range-selector">
                          <button
                            className={dateRange === 7 ? 'active' : ''}
                            onClick={() => setDateRange(7)}
                            disabled={loadingDetails}
                          >
                            7 Days
                          </button>
                          <button
                            className={dateRange === 30 ? 'active' : ''}
                            onClick={() => setDateRange(30)}
                            disabled={loadingDetails}
                          >
                            30 Days
                          </button>
                          <button
                            className={dateRange === 90 ? 'active' : ''}
                            onClick={() => setDateRange(90)}
                            disabled={loadingDetails}
                          >
                            90 Days
                          </button>
                          <button
                            className={dateRange === 365 ? 'active' : ''}
                            onClick={() => setDateRange(365)}
                            disabled={loadingDetails}
                          >
                            All Time
                          </button>
                        </div>
                      )}
                    >
                      {loadingDetails ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <div style={{ fontSize: '24px', marginBottom: '10px' }}><DSIcon name="clock" /></div>
                          <p>Loading data...</p>
                        </div>
                      ) : dailyData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart 
                            data={dailyData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={formatDate}
                              interval="preserveStartEnd"
                              minTickGap={30}
                              angle={-45}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis 
                              domain={[0, 'auto']}
                              tickFormatter={(value) => `$${value}`}
                            />
                            <Tooltip 
                              formatter={(value) => formatCurrency(value)}
                              labelFormatter={formatDate}
                            />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="expected_revenue"
                              stroke="var(--ds-color-textDisabled)"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              name="Expected Revenue"
                              dot={false}
                            />
                            <Line
                              type="monotone"
                              dataKey="cumulative_actual"
                              stroke="var(--ds-color-statusSuccessText)"
                              strokeWidth={2}
                              name="Actual Revenue"
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <p>No revenue data available for the selected time period.</p>
                      )}
                    </DSCard>

                    {/* Device Information */}
                    <DSCard cornerVariant="md" style={{ marginTop: '20px' }} title="Device Information">
                      <div className="table-responsive">
                      <table>
                        <tbody>
                          <tr>
                            <td><strong>Device Name:</strong></td>
                            <td>{device.device_name}</td>
                          </tr>
                          <tr>
                            <td><strong>Device Type:</strong></td>
                            <td>{device.device_type}</td>
                          </tr>
                          <tr>
                            <td><strong>Location:</strong></td>
                            <td>{device.location || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>Online Status:</strong></td>
                            <td>{device.isOnline ? 'Online' : 'Offline'}</td>
                          </tr>
                          <tr>
                            <td><strong>LAN IP:</strong></td>
                            <td>{device.local_ip || 'Unknown'}</td>
                          </tr>
                          <tr>
                            <td><strong>Revenue Tracking Start Date:</strong></td>
                            <td>
                              {deviceDetails.revenue_tracking_start_date 
                                ? new Date(deviceDetails.revenue_tracking_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                : 'Not set (using registration date)'}
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Registered:</strong></td>
                            <td>{new Date(deviceDetails.device_activated).toLocaleDateString()}</td>
                          </tr>
                          <tr>
                            <td><strong>Days Active:</strong></td>
                            <td>{deviceDetails.days_active}</td>
                          </tr>
                        </tbody>
                      </table>
                      </div>
                    </DSCard>
                  </div>
                )}
              </DSCard>
            );
          })}
        </div>
      ) : (
        <DSCard cornerVariant="md">
          <div className="devices-empty-state">
            <div className="devices-empty-state-icon"><DSIcon name="gamepad" /></div>
            <h3>No Devices Yet</h3>
            <p>Get started by registering your first Raspberry Pi device</p>
            <DSButton tone="primary" appearance="solid" onClick={() => setShowAddModal(true)} style={{ fontSize: '16px', padding: '12px 24px' }}>
              + Register First Device
            </DSButton>
          </div>
        </DSCard>
      )}

      {/* Floating action button for mobile */}
      <button 
        className="fab-add-device" 
        onClick={() => setShowAddModal(true)}
        aria-label="Add Device"
      >
        +
      </button>

      {showAddModal && (
        <div className="device-modal-overlay" onClick={(e) => e.target === e.currentTarget && handleCloseModal()}>
          <div className="device-modal">
            {newDeviceApiKey ? (
              <>
                <h2><DSIcon name="checkCircle" /> Device Registered!</h2>
                
                <div className="device-alert success">
                  <p><strong>Device API Key</strong></p>
                  <div className="device-api-key-display">
                    {newDeviceApiKey}
                  </div>
                  <p style={{ fontSize: '14px', marginTop: '12px' }}>
                    <DSIcon name="warning" /> <strong>IMPORTANT:</strong> Copy this API key now! It will not be shown again.
                  </p>
                </div>

                <div className="device-form-group">
                  <label>Raspberry Pi Configuration:</label>
                  <div className="device-config-code">
{`SERVER_URL=https://app.capitalpinballclub.com
DEVICE_API_KEY=${newDeviceApiKey}
DEVICE_NAME=${newDevice.deviceName}
DEVICE_TYPE=${newDevice.deviceType}
LOCATION=${newDevice.location || 'Main Room'}`}
                  </div>
                </div>

                <div className="device-modal-actions">
                  <DSButton tone="primary" appearance="solid" onClick={() => { navigator.clipboard.writeText(newDeviceApiKey); showToast('API key copied to clipboard!', 'success'); }}>
                    <DSIcon name="clipboard" /> Copy API Key
                  </DSButton>
                  <DSButton tone="secondary" appearance="solid" onClick={handleCloseModal}>
                    Done
                  </DSButton>
                </div>
              </>
            ) : (
              <>
                <h2>Register New Device</h2>
                
                <div className="device-form-group">
                  <DSInput
                    label="Device Name *"
                    type="text"
                    value={newDevice.deviceName}
                    onChange={(e) => setNewDevice({...newDevice, deviceName: e.target.value})}
                    placeholder="e.g., Pinball-Station-1"
                  />
                </div>

                <div className="device-form-group">
                  <DSSelect
                    label="Device Type *"
                    value={newDevice.deviceType}
                    onChange={(e) => setNewDevice({...newDevice, deviceType: e.target.value})}
                  >
                    <option value="pinball">Pinball</option>
                    <option value="arcade">Arcade</option>
                    <option value="board_game">Board Game</option>
                    <option value="other">Other</option>
                  </DSSelect>
                </div>

                <div className="device-form-group">
                  <DSInput
                    label="Location"
                    type="text"
                    value={newDevice.location}
                    onChange={(e) => setNewDevice({...newDevice, location: e.target.value})}
                    placeholder="e.g., Main Room"
                  />
                </div>

                <div className="device-modal-actions">
                  <DSButton tone="primary" appearance="solid" onClick={handleAddDevice}>
                    Register Device
                  </DSButton>
                  <DSButton tone="secondary" appearance="solid" onClick={handleCloseModal}>
                    Cancel
                  </DSButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showApiKeyModal && selectedDevice && (
        <div className="device-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowApiKeyModal(false)}>
          <div className="device-modal">
            <h2><DSIcon name="key" /> Device Configuration</h2>
            
            <div className="device-form-group">
              <label><strong>Device Name</strong></label>
              <p style={{ margin: '4px 0 0 0', color: 'var(--ds-color-textDefault)' }}>{selectedDevice.device_name}</p>
            </div>

            <div className="device-form-group">
              <label><strong>Device Type</strong></label>
              <p style={{ margin: '4px 0 0 0', color: 'var(--ds-color-textDefault)', textTransform: 'capitalize' }}>{selectedDevice.device_type}</p>
            </div>

            <div className="device-form-group">
              <label><strong>Location</strong></label>
              <p style={{ margin: '4px 0 0 0', color: 'var(--ds-color-textDefault)' }}>{selectedDevice.location || 'N/A'}</p>
            </div>

            <div className="device-alert info">
              <p><strong>Device API Key</strong></p>
              <div className="device-api-key-display">
                {selectedDevice.api_key}
              </div>
            </div>

            <div className="device-form-group">
              <label>Raspberry Pi Configuration:</label>
              <div className="device-config-code">
{`SERVER_URL=https://app.capitalpinballclub.com
DEVICE_API_KEY=${selectedDevice.api_key}
DEVICE_NAME=${selectedDevice.device_name}
DEVICE_TYPE=${selectedDevice.device_type}
LOCATION=${selectedDevice.location || 'Main Room'}`}
              </div>
            </div>

            <div className="device-modal-actions">
              <DSButton tone="primary" appearance="solid" onClick={() => { navigator.clipboard.writeText(selectedDevice.api_key); showToast('API key copied to clipboard!', 'success'); }}>
                <DSIcon name="clipboard" /> Copy API Key
              </DSButton>
              <DSButton tone="secondary" appearance="solid" onClick={() => { setShowApiKeyModal(false); setSelectedDevice(null); }}>
                Close
              </DSButton>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && deviceToDelete && (
        <div className="device-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowDeleteModal(false)}>
          <div className="device-modal">
            <h2 style={{ color: 'var(--ds-color-statusErrorText)' }}><DSIcon name="warning" /> Delete Device</h2>

            <div className="device-alert error">
              <p><strong>WARNING: This action cannot be undone!</strong></p>
              <p style={{ marginTop: '8px' }}>You are about to permanently delete:</p>
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--ds-color-surfaceSubtle)', borderRadius: '8px' }}>
                <p style={{ margin: '4px 0' }}><strong>Name:</strong> {deviceToDelete.device_name}</p>
                <p style={{ margin: '4px 0' }}><strong>Type:</strong> {deviceToDelete.device_type}</p>
                <p style={{ margin: '4px 0' }}><strong>Location:</strong> {deviceToDelete.location || 'N/A'}</p>
                <p style={{ margin: '4px 0' }}><strong>Total Revenue:</strong> ${parseFloat(deviceToDelete.total_revenue || 0).toFixed(2)}</p>
              </div>
              <p style={{ marginTop: '12px', fontSize: '13px' }}>
                Transaction history will remain in the system, but the device will no longer be accessible.
              </p>
            </div>

            <div className="device-form-group">
              <div style={{
                padding: '12px',
                backgroundColor: 'var(--ds-color-surface)',
                borderRadius: '8px',
                fontWeight: 700,
                marginBottom: '8px',
                wordBreak: 'break-word'
              }}>
                {deviceToDelete.device_name}
              </div>
              <DSInput
                label="To confirm, type the device name exactly:"
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type device name here"
              />
            </div>

            <div className="device-modal-actions">
              <DSButton tone="primary" appearance="solid" onClick={confirmDelete} disabled={deleteConfirmation.trim().toLowerCase() !== deviceToDelete.device_name.trim().toLowerCase()} style={{ backgroundColor: 'var(--ds-color-statusErrorText)', opacity: deleteConfirmation.trim().toLowerCase() !== deviceToDelete.device_name.trim().toLowerCase() ? 0.5 : 1, cursor: deleteConfirmation.trim().toLowerCase() !== deviceToDelete.device_name.trim().toLowerCase() ? 'not-allowed' : 'pointer' }} leadingIcon={<DSIcon name="trash" />}>Delete Permanently
              </DSButton>
              <DSButton tone="secondary" appearance="solid" onClick={() => { setShowDeleteModal(false); setDeviceToDelete(null); setDeleteConfirmation(''); }}>
                Cancel
              </DSButton>
            </div>
          </div>
        </div>
      )}

      {showLogsModal && logsDevice && (
        <div className="device-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowLogsModal(false)}>
          <div className="device-modal" style={{ maxWidth: '900px', width: '95%' }}>
            <h2><DSIcon name="scroll" /> Recent Device Logs</h2>
            <div className="device-alert info">
              <p><strong>Device:</strong> {logsDevice.device_name}</p>
              <p><strong>LAN IP:</strong> {logsDevice.local_ip || 'Unknown'}</p>
            </div>

            {logsLoading ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>Loading recent logs...</div>
            ) : recentLogs.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>No recent logs available.</div>
            ) : (
              <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid var(--ds-color-borderSubtle)', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--ds-color-surface)' }}>
                    <tr>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Time</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Level</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Event</th>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLogs.map((entry, index) => (
                      <tr key={`${entry.timestamp}-${index}`} style={{ borderTop: '1px solid var(--ds-color-borderSubtle)' }}>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap', fontSize: '12px' }}>
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'Unknown'}
                        </td>
                        <td style={{ padding: '8px', textTransform: 'uppercase', fontWeight: 600, fontSize: '12px' }}>
                          {entry.level || 'info'}
                        </td>
                        <td style={{ padding: '8px', fontSize: '12px' }}>{entry.event || 'event'}</td>
                        <td style={{ padding: '8px', fontSize: '13px' }}>{entry.message || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="device-modal-actions" style={{ marginTop: '20px' }}>
              <DSButton tone="secondary" appearance="solid" onClick={() => setShowLogsModal(false)}>
                Close
              </DSButton>
            </div>
          </div>
        </div>
      )}

      {showReconcileModal && deviceToReconcile && (
        <div className="device-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowReconcileModal(false)}>
          <div className="device-modal">
            <h2><DSIcon name="refresh" /> Reconcile Physical Play Count</h2>
            
            <div className="device-alert info">
              <p><strong>Device:</strong> {deviceToReconcile.device_name}</p>
              <p style={{ marginTop: '8px', fontSize: '14px' }}>
                Enter the physical play count shown on the pinball machine. The system will calculate 
                how many plays occurred outside of QR code usage (e.g., quarters) and add them to the device's reporting.
              </p>
            </div>

            <div className="device-form-group">
              <DSInput
                label="Physical Play Count from Machine *"
                type="number"
                min="0"
                value={reconciliationData.reportedPhysicalCount}
                onChange={(e) => setReconciliationData({...reconciliationData, reportedPhysicalCount: e.target.value})}
                placeholder="Enter the count from the machine"
              />
            </div>

            <div className="device-form-group">
              <DSTextarea
                label="Note (Optional)"
                value={reconciliationData.note}
                onChange={(e) => setReconciliationData({...reconciliationData, note: e.target.value})}
                placeholder="Add any notes about this reconciliation"
                rows="3"
              />
            </div>

            {reconciliations.length > 0 && (
              <>
                <div style={{ marginTop: '20px', marginBottom: '10px' }}>
                  <DSButton tone="secondary" appearance="solid" onClick={() => setShowReconciliationHistory(!showReconciliationHistory)} style={{ width: '100%' }}>
                    {showReconciliationHistory ? <DSIcon name="chevronDown" /> : <DSIcon name="chevronRight" />} Reconciliation History ({reconciliations.length})
                  </DSButton>
                </div>

                {showReconciliationHistory && (
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid var(--ds-color-borderSubtle)',
                    borderRadius: '8px',
                    padding: '12px',
                    backgroundColor: 'var(--ds-color-surface)'
                  }}>
                    {reconciliations.map((rec, idx) => (
                      <div key={rec.id} style={{
                        padding: '12px',
                        marginBottom: idx < reconciliations.length - 1 ? '12px' : '0',
                        backgroundColor: 'var(--ds-color-surfaceSubtle)',
                        borderRadius: '6px',
                        border: '1px solid var(--ds-color-borderSubtle)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <strong style={{ color: 'var(--ds-color-statusInfoText)' }}>
                            Physical Count: {rec.reported_physical_count}
                          </strong>
                          <span style={{ fontSize: '13px', color: 'var(--ds-color-textMuted)' }}>
                            {new Date(rec.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '14px', color: 'var(--ds-color-textMuted)' }}>
                          <div>QR Plays at Time: {rec.qr_plays_at_time}</div>
                          <div>Untracked Plays: <strong>{rec.calculated_untracked_plays}</strong></div>
                          {rec.note && <div style={{ marginTop: '6px', fontStyle: 'italic' }}>Note: {rec.note}</div>}
                          {rec.created_by_username && (
                            <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--ds-color-textDisabled)' }}>
                              By: {rec.created_by_first_name} {rec.created_by_last_name} ({rec.created_by_username})
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="device-modal-actions" style={{ marginTop: '20px' }}>
              <DSButton tone="primary" appearance="solid" onClick={submitReconciliation}>
                Submit Reconciliation
              </DSButton>
              <DSButton tone="secondary" appearance="solid" onClick={() => { setShowReconcileModal(false); setDeviceToReconcile(null); setReconciliationData({ reportedPhysicalCount: '', note: '' }); setShowReconciliationHistory(false); }}>
                Cancel
              </DSButton>
            </div>
          </div>
        </div>
      )}

      {showEditModal && deviceToEdit && (
        <div className="device-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowEditModal(false)}>
          <div className="device-modal">
            <h2><DSIcon name="edit" /> Edit Device Settings</h2>
            
            <div className="device-alert info">
              <p><strong>Device:</strong> {deviceToEdit.device_name}</p>
            </div>

            <div className="device-form-group">
              <DSInput
                label="Revenue Goal ($)"
                type="number"
                min="0.01"
                step="0.01"
                value={editData.revenueGoal}
                onChange={(e) => setEditData({...editData, revenueGoal: e.target.value})}
              />
              <div style={{ fontSize: '12px', color: 'var(--ds-color-textMuted)', marginTop: '4px' }}>
                Target revenue amount for this device. Used to calculate progress percentage and estimated days to goal.
              </div>
            </div>

            <div className="device-form-group">
              <DSInput
                label="Revenue Tracking Start Date"
                type="date"
                value={editData.revenueTrackingStartDate}
                onChange={(e) => setEditData({...editData, revenueTrackingStartDate: e.target.value})}
              />
              <div style={{ fontSize: '12px', color: 'var(--ds-color-textMuted)', marginTop: '4px' }}>
                Used to calculate daily average and estimated days to goal. Set this to when the device was first activated.
              </div>
            </div>

            <div className="device-modal-actions" style={{ marginTop: '20px' }}>
              <DSButton tone="primary" appearance="solid" onClick={submitDeviceEdit}>
                Save Changes
              </DSButton>
              <DSButton tone="secondary" appearance="solid" onClick={() => { setShowEditModal(false); setDeviceToEdit(null); }}>
                Cancel
              </DSButton>
            </div>
          </div>
        </div>
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={confirmSubmitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}

      <DeveloperBottomNav />
    </div>
  );
}

export default Devices;
